import React, {useEffect, useRef} from 'react';
import clsx from 'clsx';
import {useLocation} from 'react-router';
import {checkIsActive, KTSVG, WithChildren} from '../../../../helpers';
import {useLayout} from '../../../core';
import styles from '../sidebar.module.scss';
import {SidebarTooltip} from './SidebarTooltip';

type Props = {
    to: string;
    title: string;
    icon?: string;
    fontIcon?: string;
    hasBullet?: boolean;
    childPaths?: string[];
    biIcon?: string;
};

const SidebarMenuItemWithSub: React.FC<Props & WithChildren> = ({
    children,
    to,
    title,
    icon,
    fontIcon,
    hasBullet,
    childPaths = [],
    biIcon,
}) => {
    const {pathname} = useLocation();
    const isActive =
        checkIsActive(pathname, to) ||
        childPaths.some((path) => checkIsActive(pathname, path));
    const {config} = useLayout();
    const {app} = config;
    const itemRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isActive && itemRef.current) {
            const sub = itemRef.current.querySelector<HTMLElement>('.menu-sub');
            if (sub && sub.style.display === 'none') {
                sub.style.display = '';
            }
        }
    }, [isActive]);

    const handleMenuLinkClick = (e: React.MouseEvent) => {
        const isMinimized = document.body.getAttribute('data-kt-app-sidebar-minimize') === 'on';
        if (!isMinimized) return;

        e.preventDefault();
        e.stopPropagation();

        const toggleBtn = document.getElementById('kt_app_sidebar_toggle');
        if (toggleBtn) {
            toggleBtn.click();
        }

        setTimeout(() => {
            const menuLink = itemRef.current?.querySelector<HTMLElement>('.menu-link');
            menuLink?.click();
        }, 300);
    };

    return (
        <div
            ref={itemRef}
            className={clsx('menu-item', styles.menuItem, {'here show': isActive}, 'menu-accordion')}
            data-kt-menu-trigger="click"
        >
            <span className="menu-link" onClick={handleMenuLinkClick}>
                {hasBullet && (
                    <span className="menu-bullet">
                        <span className="bullet bullet-dot"></span>
                    </span>
                )}
                {icon && app?.sidebar?.default?.menu?.iconType === 'svg' && (
                    <SidebarTooltip title={title}>
                        <span className="menu-icon">
                            <KTSVG path={icon} className="svg-icon-2" />
                        </span>
                    </SidebarTooltip>
                )}
                {fontIcon && app?.sidebar?.default?.menu?.iconType === 'font' && (
                    <SidebarTooltip title={title}>
                        <i className={clsx('bi fs-3', fontIcon)}></i>
                    </SidebarTooltip>
                )}
                {biIcon && (
                    <SidebarTooltip title={title}>
                        <span className="menu-icon">
                            <i className={`bi ${biIcon} fs-4`}></i>
                        </span>
                    </SidebarTooltip>
                )}
                <span className="menu-title">{title}</span>
                <span className="menu-arrow"></span>
            </span>
            <div className={clsx('menu-sub menu-sub-accordion', {'menu-active-bg': isActive})}>
                {children}
            </div>
        </div>
    );
};

export {SidebarMenuItemWithSub};
